// @ts-check
/** @typedef {import('../../lib/schemas').ResolvedImage} ResolvedImage */
import React from 'react'
import Img from '../Img'

// COMPAT SHIM (task #67). OptimizedImage is now a thin wrapper over the canonical
// <Img>; it carries no image-resolution logic and imports nothing manifest-backed.
//
// Migrated call sites should pass a pre-resolved ResolvedImage descriptor as
// `image` (built server-side by lib/resolveImage / the content walker). For
// backward compatibility this also accepts a legacy { src, srcSet, placeholderSrc }
// trio and adapts it into a descriptor on the fly.
/**
 * @param {object} props
 * @param {ResolvedImage|null} [props.image]  preferred: a pre-resolved descriptor.
 * @param {string} [props.src]        LEGACY: a bare URL STRING (adapted below). A
 *   ResolvedImage descriptor passed here is a tsc error (the shape regression).
 * @param {string} [props.srcSet]     LEGACY: a srcSet STRING.
 * @param {string} [props.placeholderSrc]
 * @param {number} [props.width]
 * @param {number} [props.height]
 * @param {string} [props.alt]
 * @param {string} [props.className]
 * @param {string} [props.layout]
 * @param {string} [props.sizes]
 * @param {boolean} [props.priority]
 * @param {import('react').CSSProperties['objectFit']} [props.objectFit]
 * @param {boolean} [props.reserveSpace]
 */
function OptimizedImage({
  image,
  src,
  srcSet,
  placeholderSrc,
  width,
  height,
  alt,
  className,
  layout = 'fill',
  sizes = '100vw',
  priority = false,
  objectFit = 'contain',
  reserveSpace = true,
}) {
  // Guardrail (task #67), mirroring the <Img> unresolved-ref guard: a legacy
  // bare-string `src` with no width/height reserves no box (CLS) and skips the
  // resolver's srcSet/dims. Scream in dev so the missed seam is caught at author
  // time; migrate the call site to a resolved image={descriptor}. Zero prod cost.
  if (
    process.env.NODE_ENV !== 'production' &&
    !(image && typeof image === 'object') &&
    typeof src === 'string' &&
    (width == null || height == null)
  ) {
    console.error(
      '[OptimizedImage] received a legacy bare-string `src` ("' +
        src +
        '") without width/height — it reserves no box (CLS) and bypasses the #67 ' +
        'resolver (no srcSet/dims). Pass a resolved image={descriptor} instead.'
    )
  }

  // Prefer an explicit descriptor; else adapt legacy string props. A base64
  // placeholderSrc is dropped (task #67 keeps base64 out of CSS/HTML).
  const descriptor =
    image && typeof image === 'object'
      ? image
      : src
      ? {
          src,
          srcSet: srcSet || undefined,
          width,
          height,
          placeholderUrl:
            placeholderSrc && !placeholderSrc.startsWith('data:') ? placeholderSrc : undefined,
        }
      : null

  if (!descriptor) return null

  return (
    <Img
      image={descriptor}
      alt={alt}
      className={className}
      layout={layout}
      sizes={sizes}
      priority={priority}
      objectFit={objectFit}
      reserveSpace={reserveSpace}
    />
  )
}

export default OptimizedImage
